import React, { useMemo } from "react";
import { IDecklistV2 } from "@/models/decklist";
import { getCardData } from "@/lib/games/onePiece/getCardDataLocal";
import { Chip } from "@heroui/react";
import { getCardImage } from "@/lib/games/onePiece/getCardDataLocal";
import { IconTargetArrow, IconEyeSearch, IconBolt } from "@tabler/icons-react";
import { OnCurvePlays } from "@/components/Decklists/OnCurvePlays";
import CostCurve from "@/components/Decklists/CostCurve";
import PowerCurve from "@/components/Decklists/PowerCurve";
import KeywordStats from "@/components/Decklists/KeywordStats";
import AttributeStats from "@/components/Decklists/AttributeStats";
import TriggerBodies from "@/components/Decklists/TriggerBodies";
import {
  searcherProbability,
  formatPercentage,
  Card,
} from "@/components/Decklists/deckStatsLib";
import { getGameHandler } from "@/games";
import { SupportedGames } from "@/types/game";
import CardArt from "@/components/Card/CardArt";

export function getCardList(cards: string, gameName?: string): Card[] {
  const cardStrs = cards.split(";");

  let retCards: Card[] = [];
  // Every game has a handler, so there is no reason to special-case One Piece
  // by calling its card-data module directly.
  const handler = getGameHandler((gameName ?? "one-piece") as SupportedGames);

  for (const cardStr of cardStrs) {
    const cardCount = parseInt(cardStr.split("x")[0]);
    const cardId = cardStr.split("x")[1];

    const card = handler.getCardData(cardId);

    // The deck's identity card is not a deck slot. This used to OR together
    // two games' raw field values instead of asking the handler.
    // The deck's identity card is not a deck slot. For Riftbound that is the
    // Legend, which lives in leader_id rather than the decklist, so this is a
    // no-op there - the previous code excluded Champion Units instead, which
    // are ordinary deck cards and were being dropped from the stats.
    const isLeaderCard = card ? Boolean(handler.isLeader?.(card)) : false;
    if (card && !isLeaderCard) {
      const retCard: Card = {
        id: cardId,
        count: cardCount,
        data: card,
      };
      retCards.push(retCard);
    }
  }
  return retCards;
}

function getUniqueElements(arr: any[]) {
  // Use a Set to get unique elements
  const uniqueElements = new Set(arr);
  // Convert the Set back to an array
  return Array.from(uniqueElements);
}

function getSearchTerms(description: string): Record<string, string[]> {
  //   const str =
  //     "reveal up to 1 [Sanji] or {Big Mom Pirates} type card other than [Charlotte Pudding]";

  // Define regex patterns
  const searchPattern = /(\[[^\]]+\]|\{[^\}]+\}|\"[^\"]+\")/g;
  const ignorePattern =
    /other than ((?:\[[^\]]+\]|\{[^\}]+\}|\"[^\"]+\")(?:, (?:\[[^\]]+\]|\{[^\}]+\}|\"[^\"]+\"))*)/;

  // Initialize arrays for search and ignore terms
  const searchTerms = [];
  const ignoreTerms = [];

  // Find the ignore terms
  const ignoreMatch = description.match(ignorePattern);
  if (ignoreMatch) {
    // Split the ignore terms by ", " if multiple terms are found
    const terms = ignoreMatch[1].split(/, /);
    ignoreTerms.push(...terms);
  }

  // Find all search terms
  let match;
  while ((match = searchPattern.exec(description)) !== null) {
    // Exclude terms that match [On Play] and the ignore terms
    if (
      ![
        "[On Play]",
        "[Activate: Main]",
        "[Counter]",
        "[Main]",
        "[Blocker]",
      ].includes(match[1]) &&
      !ignoreTerms.includes(match[1])
    ) {
      searchTerms.push(match[1]);
    }
  }

  // Create the result object
  const result = {
    search: getUniqueElements(searchTerms),
    ignore: ignoreTerms,
  };

  return result;
}

function cardMatches(card: Card, searchTerm: string): boolean {
  if (searchTerm.includes("[")) {
    // exact name match
    const exactName = searchTerm.replace("[", "").replace("]", "");
    return card.data.name === exactName;
  } else if (searchTerm.includes("{")) {
    // exact type match
    const exactType = searchTerm.replace("{", "").replace("}", "");
    const subTypes = card.data.subtype.split("/");
    return subTypes.includes(exactType);
  } else if (searchTerm.includes('"')) {
    // Pattern match subtype
    const matchType = searchTerm.replaceAll('"', "");
    const subTypes = card.data.subtype.split("/");
    let anyMatch = false;
    for (const subType of subTypes) {
      if (subType.includes(matchType)) {
        anyMatch = true;
        break;
      }
    }
    return anyMatch;
  }
  return false;
}

function getSearcherTargets(
  cardList: Card[],
  searchTerms: Record<string, string[]>,
  cardId?: string,
): number {
  let targets = 0;

  if (cardId === "OP03-008") {
    // 1c Red Buggy. Searches for Event cards
    for (const card of cardList) {
      let shouldAdd = false;
      if (
        card.data.type === "Event" &&
        card.data.cost !== undefined &&
        card.data.cost <= 1
      ) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "OP02-005") {
    // 2c Curly.Dadan. Searches 1 red character with a cost of 1
    for (const card of cardList) {
      let shouldAdd = false;
      if (
        card.data.type === "Character" &&
        card.data.cost !== undefined &&
        card.data.cost <= 1
      ) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "OP09-050") {
    // 1c Nami. Searches top 5 for 1 blue Event
    for (const card of cardList) {
      let shouldAdd = false;
      if (card.data.type === "Event" && card.data.color === "Blue") {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "OP09-117") {
    // 3c Dereshi! Searches top 5; reveal up to 2 cards with Trigger other than Dereshi!
    // and add to hand
    for (const card of cardList) {
      let shouldAdd = false;
      if (card.data.number !== "OP09-117" && card.data.trigger) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "OP12-034") {
    // 1c Perona. If your Leader has <Slash>, look at 5; reveal up to 1 <Slash> attribute card or green Event
    for (const card of cardList) {
      let shouldAdd = false;
      if (
        card.data.attribute === "Slash" ||
        (card.data.type === "Event" && card.data.color === "Green")
      ) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "OP13-016") {
    // If Leader is [Sabo], [Portgas.D.Ace] or [Monkey.D.Luffy], look at 4; reveal up to 1 card with cost 3 or more
    for (const card of cardList) {
      let shouldAdd = false;
      if (card.data.cost !== undefined && (card.data.cost as number) >= 3) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "OP13-113") {
    // Look at 4; reveal up to 1 card with Trigger other than [Lilith]
    for (const card of cardList) {
      let shouldAdd = false;
      if (card.data.name !== "Lilith" && card.data.trigger) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  } else if (cardId === "EB03-060") {
    // If Leader is [Nami], look at 4; reveal up to 1 card with cost 2 to 8
    for (const card of cardList) {
      let shouldAdd = false;
      if (
        card.data.cost !== undefined &&
        (card.data.cost as number) >= 2 &&
        (card.data.cost as number) <= 8
      ) {
        shouldAdd = true;
      }
      if (shouldAdd) {
        targets += card.count;
      }
    }
    return targets;
  }

  for (const card of cardList) {
    let shouldIgnore = false;
    let shouldAdd = false;
    for (const ignoreTerm of searchTerms.ignore) {
      if (cardMatches(card, ignoreTerm)) {
        shouldIgnore = true;
        break; // No need to continue checking ignore terms
      }
    }
    if (shouldIgnore) {
      continue; // Skip this card
    }

    for (const searchTerm of searchTerms.search) {
      if (cardMatches(card, searchTerm)) {
        shouldAdd = true;
        break;
      }
    }

    if (shouldAdd) {
      if (card.id === cardId) {
        // remove 1 because by searching, you cannot grab this card
        targets += card.count - 1;
      } else {
        targets += card.count;
      }
    }
  }

  return targets;
}

function Searchers({
  searchers,
  onCardPress,
}: {
  searchers: any[];
  onCardPress?: (cardId: string) => void;
}) {
  if (searchers.length === 0) {
    return <></>;
  }

  let display = [];

  const sortedEntries = searchers.sort((a, b) => b.probability - a.probability);

  for (const searcher of sortedEntries) {
    display.push(
      <div key={searcher.name} className="">
        <div className="flex flex-col items-center">
          <div
            className="cursor-pointer"
            onClick={() => onCardPress?.(searcher.id)}
          >
            <CardArt
              alt={searcher.name}
              src={getCardImage(searcher.id)}
              width={100}
              radiusClassName="rounded-[4px]"
            />
          </div>
          {/* <div className="line-clamp-1 text-center">{searcher.name}</div> */}
          <div className="text-lg mt-2">
            {formatPercentage(searcher.probability)}
          </div>
          <div className="text-sm text-default-500 flex items-center gap-1">
            <IconEyeSearch width={18} height={18} />
            Search: {searcher.cardsToSearch}
          </div>
          <div className="text-sm text-default-500 flex items-center gap-1">
            <IconTargetArrow width={18} height={18} />
            Hits: {searcher.searcherTargets}
          </div>
        </div>
      </div>,
    );
  }
  return (
    <div className="">
      <div className="mb-2 text-lg font-semibold">Searcher Hit Chance</div>
      <div>
        <div className="flex flex-wrap justify-around md:justify-normal gap-4">
          {display}
        </div>
      </div>
    </div>
  );
}

export function getSearchers(cardList: Card[]) {
  let searchers = [];
  const regex = /Look at(?: up to)? (\d+) cards from the top of your deck/i;
  for (const card of cardList) {
    const match = card.data.description.match(regex);
    if (match) {
      if (
        card.data.description.includes("reveal up") ||
        card.data.description.includes("play up")
      ) {
        // Removes organizers like 3c Blue Doffy
        const number = parseInt(match[1], 10);
        const searchTerms = getSearchTerms(card.data.description);
        const searcherTargets = getSearcherTargets(
          cardList,
          searchTerms,
          card.data.number,
        );
        searchers.push({
          id: card.id,
          count: card.count,
          name: card.data.name,
          cardsToSearch: number,
          searchTerms: searchTerms,
          cardDescription: card.data.description,
          searcherTargets: searcherTargets,
          probability: searcherProbability(searcherTargets, number),
        });
      }
    }
  }
  return searchers;
}

function SubTypeChips({
  subTypeCounts,
}: {
  subTypeCounts: Record<string, number>;
}) {
  const entries = Object.entries(subTypeCounts);
  entries.sort((a, b) => b[1] - a[1]);
  //   const sortedArray = entries.map((entry) => entry[1]);
  //   const display = [];
  //   console.lo
  //   for (const entry of sortedArray) {
  //     console.log(entry);
  //     // display.push(<Chip>{en}</Chip>)
  //   }
  let display = [];
  for (const entry of entries) {
    display.push(
      <Chip key={entry[0]} className="bg-default-100">
        {entry[0]}: {entry[1]}
      </Chip>,
    );
  }
  return (
    <div className="">
      <div className="font-semibold text-lg">Types</div>
      <div className="flex flex-wrap gap-2 mt-2">{display}</div>
    </div>
  );
}

function getCostDict(cardList: Card[]) {
  let costDict: Record<string, number> = {
    "0": 0,
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0,
    "6": 0,
    "7": 0,
    "8": 0,
    "9": 0,
    "10+": 0,
  };
  for (const card of cardList) {
    if (card.data.cost) {
      const costStr = card.data.cost.toString();
      if (card.data.cost >= 10) {
        costDict["10+"] += card.count;
      } else {
        costDict[costStr] += card.count;
      }
    }
  }
  return costDict;
}

function getPowerDict(cardList: Card[]) {
  let powerDict: Record<string, number> = {
    "0": 0,
    "1k": 0,
    "2k": 0,
    "3k": 0,
    "4k": 0,
    "5k": 0,
    "6k": 0,
    "7k": 0,
    "8k": 0,
    "9k": 0,
    "10k+": 0,
  };
  for (const card of cardList) {
    if (card.data.power) {
      const power = card.data.power;
      if (card.data.power >= 10000) {
        powerDict["10k+"] += card.count;
      } else {
        let powerStr = power / 1000 + "k";
        if (power === 0) {
          powerStr = "0";
        }
        powerDict[powerStr] += card.count;
      }
    }
  }
  return powerDict;
}

function getAttributeDict(cardList: Card[]) {
  let data: Record<string, number> = {};
  for (const card of cardList) {
    if (card.data.attribute && card.data.attribute !== "-") {
      if (!(card.data.attribute in data)) {
        data[card.data.attribute] = card.count;
      } else {
        data[card.data.attribute] += card.count;
      }
    }
  }
  return data;
}

function getKeywordDict(cardList: Card[]) {
  let keywordDict: Record<string, number> = {};
  let keywords = [
    "[Blocker]",
    "[Activate: Main]",
    "[Rush]",
    "[On Play]",
    "DON!! −",
    "[Main]",
  ];
  for (const card of cardList) {
    for (const keyword of keywords) {
      if (card.data.description.includes(keyword)) {
        keywordDict[keyword] = (keywordDict[keyword] ?? 0) + card.count;
      }
    }
    if (card.data.trigger) {
      keywordDict["[Trigger]"] = (keywordDict["[Trigger]"] ?? 0) + card.count;
    }
  }

  return keywordDict;
}

function CounterStats({
  counterDict,
}: {
  counterDict: Record<string, number>;
}) {
  function getAvgCounter(counterDict: Record<string, number>) {
    let counterTotal = 0;
    for (const [name, count] of Object.entries(counterDict)) {
      if (name.includes(" Event")) {
        const counter = parseInt(name.replace("k Event", ""));
        counterTotal += counter * count;
      } else {
        const counter = parseInt(name.replace("k", ""));
        counterTotal += counter * count;
      }
    }
    return counterTotal / 50;
  }

  let avgCounter = getAvgCounter(counterDict);

  let displayEvents = [];
  const sortedEntries = Object.entries(counterDict).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  for (const [k, v] of sortedEntries) {
    if (!["2k", "1k"].includes(k)) {
      displayEvents.push(
        <Chip key={k} className="bg-default-100">
          {k}: {v}
        </Chip>,
      );
    }
  }
  return (
    <div>
      <div className="mb-2 text-lg font-semibold">Counter Stats</div>
      <div className="flex flex-wrap gap-2">
        <Chip
          startContent={<IconBolt width={18} height={18} />}
          color="danger"
          variant="flat"
        >
          Avg: {avgCounter}k / card
        </Chip>
        {"2k" in counterDict && (
          <Chip className="bg-default-100">2k: {counterDict["2k"]}</Chip>
        )}
        {"1k" in counterDict && (
          <Chip className="bg-default-100">1k: {counterDict["1k"]}</Chip>
        )}
        {displayEvents}
      </div>
      {/* <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
        {JSON.stringify(counterDict, null, 2)}
      </pre> */}
    </div>
  );
}

function getTriggerBodiesDict(cardList: Card[]) {}

function getCounterDict(cardList: Card[]) {
  function getKey(powerGains: number[]) {
    const sum = powerGains.reduce(
      (accumulator: number, currentValue: number) => accumulator + currentValue,
      0,
    );
    return `${sum / 1000}k Event`;
  }
  let counterDict: Record<string, number> = {};
  for (const card of cardList) {
    if (
      card.data.type === "Event" &&
      card.data.description.includes("[Counter]")
    ) {
      const regex = /gains \+(\d+) power|gains an additional \+(\d+) power/gi;

      let match;
      let powerGains = [];

      while ((match = regex.exec(card.data.description)) !== null) {
        // match[1] is for "gains +3000 power" pattern
        // match[2] is for "gains an additional +2000 power" pattern
        if (match[1]) {
          powerGains.push(parseInt(match[1], 10));
        } else if (match[2]) {
          powerGains.push(parseInt(match[2], 10));
        }
      }
      if (powerGains.length > 0) {
        const key = getKey(powerGains);
        counterDict[key] = (counterDict[key] ?? 0) + card.count;
      }
    }

    if (card.data.counter && card.data.counter > 0) {
      if (card.data.counter === 1000) {
        counterDict["1k"] = (counterDict["1k"] ?? 0) + card.count;
      }
      if (card.data.counter === 2000) {
        counterDict["2k"] = (counterDict["2k"] ?? 0) + card.count;
      }
    }
  }

  return counterDict;
}

export default function DeckStats({
  leaderLife,
  decklist,
  onCardPress,
}: {
  leaderLife: number | undefined;
  decklist: string;
  onCardPress?: (cardId: string) => void;
}) {
  const cards = decklist;
  const cardList = useMemo(() => getCardList(cards), [cards]);
  const subTypeCounts = useMemo(() => {
    let counts: Record<string, number> = {};
    for (const card of cardList) {
      const subTypes = card.data.subtype.split("/");
      for (const st of subTypes) {
        if (!(st in counts)) {
          counts[st] = card.count;
        } else {
          counts[st] = counts[st] + card.count;
        }
      }
    }
    return counts;
  }, [cardList]);

  const searchers = useMemo(() => getSearchers(cardList), [cardList]);
  const costDict = useMemo(() => getCostDict(cardList), [cardList]);
  const powerDict = useMemo(() => getPowerDict(cardList), [cardList]);
  const keywordDict = useMemo(() => getKeywordDict(cardList), [cardList]);
  const counterDict = useMemo(() => getCounterDict(cardList), [cardList]);
  const attributeDict = useMemo(() => getAttributeDict(cardList), [cardList]);
  const triggerBodiesDict = useMemo(
    () => getTriggerBodiesDict(cardList),
    [cardList],
  );

  return (
    <div>
      {cardList.length > 0 ? (
        <div>
          <div className="text-3xl font-semibold mt-10">Deck Stats</div>
          <div className="flex flex-wrap gap-8 mt-4">
            <div className="flex flex-col gap-8">
              <OnCurvePlays costDict={costDict} />
              <CounterStats counterDict={counterDict} />
              {searchers.length > 0 && (
                <div className="">
                  <Searchers searchers={searchers} onCardPress={onCardPress} />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TriggerBodies life={leaderLife} cardList={cardList} />
                <div className="">
                  <CostCurve costDict={costDict} />
                </div>
                <div className="">
                  <PowerCurve powerDict={powerDict} />
                </div>
              </div>
              <SubTypeChips subTypeCounts={subTypeCounts} />
              <AttributeStats data={attributeDict} />
              <KeywordStats keywordDict={keywordDict} />
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export function getCardTypes(
  decklist: IDecklistV2,
  gameName?: string,
): string[] {
  // Returns top 3 card types/tags
  if (!decklist.decklist) {
    return [];
  }
  const cardList = getCardList(decklist.decklist, gameName);
  let counts: Record<string, number> = {};
  for (const card of cardList) {
    let typesToCount: string[] = [];

    // For Palworld, use type (Dragon, Ice, etc.) and aptitude (Harvesting, etc.)
    if (gameName === "palworld") {
      const palworldCard = card.data as any;
      if (palworldCard.type && Array.isArray(palworldCard.type)) {
        typesToCount.push(...palworldCard.type);
      }
      if (palworldCard.aptitude && Array.isArray(palworldCard.aptitude)) {
        typesToCount.push(...palworldCard.aptitude);
      }
    }
    // For Riftbound, use tags instead of subtypes
    else if (gameName === "riftbound" && (card.data as any).tags) {
      typesToCount = (card.data as any).tags;
    } else if ((card.data as any).subtype) {
      // For One Piece and Union Arena, use subtypes
      typesToCount = (card.data as any).subtype.split("/");
    }

    for (const st of typesToCount) {
      if (!(st in counts)) {
        counts[st] = card.count;
      } else {
        counts[st] = counts[st] + card.count;
      }
    }
  }

  const topThree = Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([key]) => key);
  return topThree;
}
